import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../services/api';
import NewClientDetailModal from '../components/NewClientDetailModal';

// Office intake tracker: every new-client request from booking to resolution.
const DECISION_CHIP = {
  pending: { label: 'Pending', bg: 'var(--steel-blue)' },
  accepted: { label: 'Accepted', bg: 'var(--success-color)' },
  rejected: { label: 'Rejected', bg: 'var(--error-color)' },
  info_admin: { label: 'Office follow-up', bg: 'var(--accent-color)' },
  info_self: { label: 'Therapist reaching out', bg: 'var(--accent-color)' },
};
const OFFICE_CHIP = {
  completed: { label: 'Intake completed', bg: 'var(--success-color)' },
  referred_out: { label: 'Referred out', bg: 'var(--muted-color)' },
  reassigned: { label: 'Reassigned', bg: 'var(--primary-color)' },
};

export default function AdminNewClientRequestsPage() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [range, setRange] = useState('90'); // '30'|'90'|'365'|'all'
  const [statusFilter, setStatusFilter] = useState('');
  const [therapistFilter, setTherapistFilter] = useState('');
  const [q, setQ] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [therapists, setTherapists] = useState([]);

  const load = () => {
    const params = {};
    if (range === 'all') params.all = '1';
    else {
      const from = new Date();
      from.setDate(from.getDate() - Number(range));
      params.from = from.toISOString().slice(0, 10);
    }
    if (statusFilter) params.status = statusFilter;
    if (therapistFilter) params.therapistId = therapistFilter;
    if (q.trim()) params.q = q.trim();
    if (showClosed) params.includeClosed = '1';
    if (['completed', 'referred_out'].includes(statusFilter)) params.includeClosed = '1';
    setError('');
    API.get('/admin/new-client-requests', { params })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError("Couldn't load requests — try reloading."));
  };

  useEffect(() => { load(); /* re-run on any filter change */ }, [range, statusFilter, therapistFilter, showClosed]);

  useEffect(() => {
    let active = true;
    Promise.all([
      API.get('/admin/therapist-profiles').catch(() => ({ data: [] })),
      API.get('/acuity/all-summaries').catch(() => ({ data: [] })),
    ]).then(([profiles, summaries]) => {
      if (!active) return;
      const calByTherapist = {};
      (summaries.data || []).forEach((s) => { calByTherapist[s.therapistUserId] = s.calendarId; });
      setTherapists(
        (profiles.data || []).map((p) => ({
          id: p.therapist_id,
          name: `${p.first_name} ${p.last_name}`,
          calendarId: calByTherapist[p.therapist_id] || null,
        }))
      );
    });
    return () => { active = false; };
  }, []);

  const counts = useMemo(() => {
    const c = { pending: 0, awaiting: 0, completed: 0, referred: 0, reassigned: 0 };
    (rows || []).forEach((r) => {
      if (r.officeStatus === 'completed') c.completed += 1;
      else if (r.officeStatus === 'referred_out') c.referred += 1;
      else if (r.officeStatus === 'reassigned') c.reassigned += 1;
      else if (r.status === 'pending') c.pending += 1;
      else if (r.status === 'accepted') c.awaiting += 1;
    });
    return c;
  }, [rows]);

  const filtered = (rows || []).filter((r) =>
    !q.trim() || r.clientName.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <h2 style={{ marginTop: 0 }}>New Client Requests</h2>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        {[['Pending', counts.pending, 'var(--steel-blue)'], ['Awaiting intake', counts.awaiting, 'var(--accent-color)'],
          ['Completed', counts.completed, 'var(--success-color)'], ['Referred out', counts.referred, 'var(--muted-color)'],
          ['Reassigned', counts.reassigned, 'var(--primary-color)']].map(([label, n, bg]) => (
          <div key={label} style={{ background: 'var(--white)', border: '1px solid var(--border-color)', borderLeft: `4px solid ${bg}`, borderRadius: 10, padding: '8px 14px', minWidth: 120 }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{n}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select style={filterSel} value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
          <option value="all">All time</option>
        </select>
        <select style={filterSel} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="info_admin">Office follow-up</option>
          <option value="info_self">Therapist reaching out</option>
          <option value="completed">Intake completed</option>
          <option value="referred_out">Referred out</option>
          <option value="reassigned">Reassigned</option>
        </select>
        <select style={filterSel} value={therapistFilter} onChange={(e) => setTherapistFilter(e.target.value)}>
          <option value="">All therapists</option>
          {therapists.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input
          style={{ ...filterSel, minWidth: 200 }}
          placeholder="Search client name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          Show completed / referred
        </label>
      </div>

      {error && <div style={{ background: '#f8d7da', color: '#721c24', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>{error}</div>}
      {rows === null && !error && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}
      {rows !== null && filtered.length === 0 && <div style={{ color: 'var(--text-muted)' }}>No requests match these filters.</div>}

      {filtered.length > 0 && (
        <div style={{ background: 'var(--white)', border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--surface-muted)', textAlign: 'left' }}>
                {['Client', 'Contact', 'Appointment', 'Therapist', 'Decision', 'Office status', 'Updated'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const d = DECISION_CHIP[r.status] || DECISION_CHIP.pending;
                const o = r.officeStatus ? OFFICE_CHIP[r.officeStatus] : null;
                return (
                  <tr
                    key={r.acuityId}
                    onClick={() => setDetailId(r.acuityId)}
                    title="Open details and office actions"
                    style={{ borderTop: '1px solid var(--surface-muted)', cursor: 'pointer' }}
                  >
                    <td style={cellStyle}><strong>{r.clientName || 'Unknown'}</strong></td>
                    <td style={cellStyle}>{r.clientEmail || '—'}</td>
                    <td style={cellStyle}>{r.apptDatetime ? new Date(r.apptDatetime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                    <td style={cellStyle}>{r.therapistName}</td>
                    <td style={cellStyle}><span style={chip(d.bg)}>{d.label}</span></td>
                    <td style={cellStyle}>
                      {o ? (
                        <span style={chip(o.bg)}>
                          {o.label}{r.officeStatus === 'reassigned' && r.reassignedToName ? ` → ${r.reassignedToName}` : ''}
                        </span>
                      ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      {r.officeStatus === 'reassigned' && !r.appointmentMoved && (
                        <span style={{ marginLeft: 6, background: '#fff3cd', color: '#7a5b00', borderRadius: 999, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>not moved</span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {r.officeUpdatedAt
                        ? `${new Date(r.officeUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${r.officeUpdatedByName ? ` · ${r.officeUpdatedByName}` : ''}`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailId && (
        <NewClientDetailModal
          acuityId={detailId}
          onClose={() => setDetailId(null)}
          officeActions
          therapists={therapists}
          onChanged={load}
        />
      )}
    </div>
  );
}

const filterSel = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, background: 'var(--white)', color: 'var(--text-strong)' };
const cellStyle = { padding: '10px 12px', verticalAlign: 'top' };
const chip = (bg) => ({ background: bg, color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' });
